import type { SelectedUser } from "Components/Chat/ChatSelectedMembers";
import { useCanInitiateChatWith } from "Hooks/Chat/useCanInitiateChatWith";

/** Why a person cannot be picked for a conversation — see {@link useSelectedUserIneligibility}. */
export type SelectedUserIneligibility = "customerWritesFirst" | "notFullMoone";

/**
 * The one place that decides whether a person cannot be picked for a conversation — picked
 * already, or still a search result — and why. Returns the question as a function because the
 * answer depends on whom it is asked about; the answer is the reason, or undefined for an
 * eligible person, so callers can ask "whether" and "why" of the same call.
 *
 * Two rules today: a business persona cannot bring a customer in (App ADR-0015), and a
 * verified-only conversation admits Full Moone personas only (App ADR-0012; an unknown tier
 * passes, the server decides). A new rule is added here and every picker follows.
 */
export const useSelectedUserIneligibility = (isFullMooneRequired = false) => {
  const canInitiateChatWith = useCanInitiateChatWith();

  return ({
    accessRoleCode,
    isFullMoone,
  }: Pick<SelectedUser, "accessRoleCode" | "isFullMoone">):
    | SelectedUserIneligibility
    | undefined => {
    if (!canInitiateChatWith(accessRoleCode)) {
      return "customerWritesFirst";
    }

    if (isFullMooneRequired && isFullMoone === false) {
      return "notFullMoone";
    }

    return undefined;
  };
};
